import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext'; // Ensure this path is correct
import { fetchSchedulesByWeek, fetchMyAssignedShifts ,fetchSchedulesInRange} from '../api'; // Ensure this path is correct
import { parse, format, isValid, isBefore, isAfter, isWithinInterval, addDays } from 'date-fns';
import { toZonedTime } from 'date-fns-tz'; // Make sure you have date-fns-tz installed (npm install date-fns-tz)
import '@fontsource/poppins'; // Make sure you have @fontsource/poppins installed (npm install @fontsource/poppins)
import BarGraph from '../components/BarGraph';
import LocationGraph from '../components/LocationGraph';
import TotalHours from '../components/TotalHours';

const MePage = () => {
    const { user, isLoading } = useAuth();
    const navigate = useNavigate();
    const [schedules, setSchedules] = useState([]); // For user's assigned schedules today
    const [openSchedules, setOpenSchedules] = useState([]); // For general open schedules overview
    const [weeklyUpcomingSchedules, setWeeklyUpcomingSchedules] = useState([]); // New state for weekly schedules
    const [error, setError] = useState(null);
    const [subscriptionMessage, setSubscriptionMessage] = useState(null); // Assuming you might have subscription messages
    const [tomorrowSchedules, setTomorrowSchedules] = useState([]);
    const [showTomorrow, setShowTomorrow] = useState(false);
    const [selectedDate, setSelectedDate] = useState(() => new Date());
const [schedulesForDate, setSchedulesForDate] = useState([]);



    // Get today's date in IST for consistency
    const today = format(toZonedTime(new Date(), 'Europe/London'), 'yyyy-MM-dd');
    const todayDateObj = parse(today, 'yyyy-MM-dd', new Date()); // standardize
const tomorrow = format(addDays(todayDateObj, 1), 'yyyy-MM-dd');

const getDateKey = (date) => format(date, 'yyyy-MM-dd'); // for API
const getDisplayDate = (date) => format(date, 'dd/MM/yyyy'); // for heading



    const isAdmin=user?.role === 'admin' || user?.role === 'manager';


    // Helper function to determine schedule status (Completed, Upcoming, In Progress, Unknown)
    const getScheduleStatus = (schedule) => {
        if (!schedule.start || !schedule.end || !schedule.date) {
    console.warn(`Missing date or times for schedule ${schedule.id}:`, schedule);
    return 'Unknown';
  }

  try {
    const currentTime = new Date();
    const startDateTime = new Date(`${schedule.date}T${schedule.start}:00.000Z`);
    let endDate = new Date(schedule.date);
    if (schedule.end < schedule.start) {
      endDate.setDate(endDate.getDate() + 1);
    }
    const endDateTime = new Date(`${endDate.toISOString().split('T')[0]}T${schedule.end}:00.000Z`);

    if (isNaN(startDateTime.getTime()) || isNaN(endDateTime.getTime())) {
      console.warn(`Invalid date/time for schedule ${schedule.id}:`, schedule);
      return 'Unknown';
    }

    if (endDateTime < currentTime) {
      return 'Completed';
    }

    if (currentTime < startDateTime) {
      return 'Upcoming';
    }

    if (currentTime >= startDateTime && currentTime < endDateTime) {
      return 'In Progress';
    }

    
    return 'Unknown';
  } catch (e) {
    console.error(`Error deriving status for schedule ${schedule.id}:`, e);
    return 'Unknown';
  }
    };


    // useEffect hook to fetch schedules when user data or loading state changes
    useEffect(() => {
        const fetchAndFilterSchedules = async () => {
            if (!user || !user.id) {
                setError('Please log in to view schedules');
                return;
            }
            setError(null); // Clear previous errors

            try {

                // Helper to get today's date in 'yyyy-MM-dd' IST format
                const getTodayDate = () => {
                    const todayDate = toZonedTime(new Date(), 'Asia/Kolkata');
                    return format(todayDate, 'yyyy-MM-dd');
                };

                // Helper to get a future date (e.g., 12 months from now) in 'yyyy-MM-dd' IST format
                const getFutureDate = (months) => {
                    const futureDate = toZonedTime(new Date(), 'Asia/Kolkata');
                    futureDate.setMonth(futureDate.getMonth() + months);
                    return format(futureDate, 'yyyy-MM-dd');
                };

                // --- Fetch All Schedules in Range for Open Schedules and Today's Schedules ---
                // Admins/Managers fetch a broad range to see all potential shifts
                const startFetchDate = getTodayDate();
                const endFetchDate = getFutureDate(12); // Fetch schedules for the next 12 months

                // This call fetches all schedules within the range, which will be filtered client-side
                // for "What's Happening Today" and "Open Schedules" based on user role and assignment.
                const response = await fetchSchedulesByWeek(startFetchDate);
                const allFetchedSchedules = response.data.data || [];

                // --- Logic for Open Schedules Block ---
                const currentOpenSchedules = allFetchedSchedules
                    .filter(schedule => {
                        const scheduleDateObj = new Date(schedule.date);
                        const todayDateObj = new Date(today);
                        return (
                            // Schedule date must be today or in the future
                            (schedule.date && scheduleDateObj >= todayDateObj) &&
                            // Schedule must not be assigned to any care worker (or assigned array is empty)
                            (!schedule.careWorker ||
                                (Array.isArray(schedule.careWorker) && schedule.careWorker.length === 0)) &&
                            // Schedule must be explicitly marked as not published (i.e., open)
                            schedule.isPublished === false
                        );
                    })
                    .map(schedule => ({
                        id: schedule._id,
                        title: schedule.description || 'Open Shift',
                        start: schedule.start || '',
                        end: schedule.end || '',
                        location: schedule.location?.name || schedule.location || 'Unknown', // Handle nested location object
                        date: schedule.date,
                        breakDuration: schedule.break ? `${schedule.break} mins` : '',
                        status: getScheduleStatus({ // Derive status for display
                            id: schedule._id,
                            start: schedule.start,
                            end: schedule.end,
                            date: schedule.date
                        })
                    }));
                setOpenSchedules(currentOpenSchedules);

                // --- Logic for User's Assigned Schedules (What's Happening & Today's Detailed Schedule) ---
                const userAssignedSchedulesToday = allFetchedSchedules
                    .filter(schedule => {
                        // Filter for schedules happening specifically today
                        if (!schedule.date || schedule.date !== today) {
                            return false;
                        }

                        if (user.role === 'admin' || user.role === 'manager') {
                            // Admins/Managers see all schedules for today
                            return true;
                        } else {
                            // Care workers/employees only see their own assigned schedules for today
                            if (!schedule.careWorker) {
                                return false; // If no careWorker assigned, it's not for this user
                            }
                            if (Array.isArray(schedule.careWorker)) {
                                // Check if user's ID is in the careWorker array (can be object or string ID)
                                return schedule.careWorker.some(cw =>
                                    (typeof cw === 'object' && cw?._id === user.id) ||
                                    (typeof cw === 'string' && cw === user.id)
                                );
                            }
                            // If careWorker is a single object or string ID
                            return (typeof schedule.careWorker === 'object' && schedule.careWorker?._id === user.id) ||
                                (typeof schedule.careWorker === 'string' && schedule.careWorker === user.id);
                        }
                    })
                    .map(schedule => {
                        // Determine care worker names for display
                        const careWorkerNames = Array.isArray(schedule.careWorker)
                            ? schedule.careWorker
                                .map(cw => {
                                    if (typeof cw === 'object' && (cw?.firstName || cw?.lastName)) {
                                        return `${cw.firstName || ''} ${cw.lastName || ''}`.trim();
                                    }
                                    return 'Unknown'; // Fallback for invalid worker object
                                })
                                .filter(Boolean) // Remove any empty strings from failed mappings
                            : (schedule.careWorker?.firstName // If single careWorker object
                                ? [`${schedule.careWorker.firstName} ${schedule.careWorker.lastName || ''}`.trim()]
                                : ['Unknown']); // Fallback if single careWorker is not an object with name

                        return {
                            id: schedule._id,
                            title: schedule.description || 'Shift',
                            start: schedule.start || '',
                            end: schedule.end || '',
                            location: schedule.location?.name || schedule.location || 'Unknown',
                            date: schedule.date,
                            breakDuration: schedule.break ? `${schedule.break} mins` : '',
                            // Ensure careWorkerNames is never empty or just ['Unknown'] if valid names exist
                            careWorkerNames: careWorkerNames.length > 0 && careWorkerNames.some(name => name !== 'Unknown')
                                ? careWorkerNames
                                : ['Unknown'],
                            status: getScheduleStatus({
                                id: schedule._id,
                                start: schedule.start,
                                end: schedule.end,
                                date: schedule.date
                            })
                        };
                    });
                setSchedules(userAssignedSchedulesToday);

                const userAssignedSchedulesTomorrow = allFetchedSchedules
  .filter(schedule => {
    if (!schedule.date || schedule.date !== tomorrow) return false;

    if (user.role === 'admin' || user.role === 'manager') {
      return true;
    }

    if (!schedule.careWorker) return false;

    if (Array.isArray(schedule.careWorker)) {
      return schedule.careWorker.some(cw =>
        (typeof cw === 'object' && cw?._id === user.id) ||
        (typeof cw === 'string' && cw === user.id)
      );
    }

    return (typeof schedule.careWorker === 'object' && schedule.careWorker?._id === user.id) ||
      (typeof schedule.careWorker === 'string' && schedule.careWorker === user.id);
  })
  .map(schedule => {
    const careWorkerNames = Array.isArray(schedule.careWorker)
      ? schedule.careWorker.map(cw =>
          typeof cw === 'object' && (cw?.firstName || cw?.lastName)
            ? `${cw.firstName || ''} ${cw.lastName || ''}`.trim()
            : 'Unknown'
        ).filter(Boolean)
      : (schedule.careWorker?.firstName
        ? [`${schedule.careWorker.firstName} ${schedule.careWorker.lastName || ''}`.trim()]
        : ['Unknown']);

    return {
      id: schedule._id,
      title: schedule.description || 'Shift',
      start: schedule.start || '',
      end: schedule.end || '',
      location: schedule.location?.name || schedule.location || 'Unknown',
      date: schedule.date,
      breakDuration: schedule.break ? `${schedule.break} mins` : '',
      careWorkerNames: careWorkerNames.length > 0 && careWorkerNames.some(name => name !== 'Unknown')
        ? careWorkerNames
        : ['Unknown'],
      status: getScheduleStatus({
        id: schedule._id,
        start: schedule.start,
        end: schedule.end,
        date: schedule.date
      })
    };
  });

setTomorrowSchedules(userAssignedSchedulesTomorrow);



                // --- Logic for Weekly Upcoming Schedules ---
                let weeklyShiftsToProcess = [];
                // Calculate the end date for the 7-day window (today + 6 days)
                const sevenDaysFromNow = format(addDays(toZonedTime(new Date(), 'Asia/Kolkata'), 6), 'yyyy-MM-dd');
                const todayDateObj = parse(today, 'yyyy-MM-dd', new Date());

                if (user.role === 'admin' || user.role === 'manager') {
                    // Admins/Managers see all upcoming shifts for the next 7 days from the broad fetch
                    weeklyShiftsToProcess = allFetchedSchedules;
                } else if (user.role === 'careWorker' || user.role === 'employee') {
                    const assignedResponse = await fetchMyAssignedShifts();
                    const allMyAssignedShifts = assignedResponse.data.data || [];

                    weeklyShiftsToProcess = allMyAssignedShifts.filter(schedule => {
                        const scheduleDate = parse(schedule.date, 'yyyy-MM-dd', new Date());
                        // Check if the schedule date is today or in the future
                        const isTodayOrFuture = isAfter(scheduleDate, todayDateObj) || format(scheduleDate, 'yyyy-MM-dd') === format(todayDateObj, 'yyyy-MM-dd');
                        const isWithinNextSevenDays = isBefore(scheduleDate, addDays(todayDateObj, 7));

                        return isTodayOrFuture && isWithinNextSevenDays;
                    });
                }

                const filteredWeeklyUpcoming = weeklyShiftsToProcess
                    .filter(schedule => {
                        // Filter for only truly 'Upcoming' shifts (not completed or in progress)
                        const status = getScheduleStatus({
                            id: schedule._id,
                            start: schedule.start,
                            end: schedule.end,
                            date: schedule.date
                        });
                        return status === 'Upcoming';
                    })
                    .map(schedule => {
                        // Determine care worker names for display, similar to the 'Today's Schedule' logic
                        const careWorkerNames = Array.isArray(schedule.careWorker)
                            ? schedule.careWorker
                                .map(cw => {
                                    if (typeof cw === 'object' && (cw?.firstName || cw?.lastName)) {
                                        return `${cw.firstName || ''} ${cw.lastName || ''}`.trim();
                                    }
                                    return 'Unknown';
                                })
                                .filter(Boolean)
                            : (schedule.careWorker?.firstName
                                ? [`${schedule.careWorker.firstName} ${schedule.careWorker.lastName || ''}`.trim()]
                                : ['Unknown']);

                        return {
                            id: schedule._id,
                            title: schedule.description || 'Shift',
                            start: schedule.start || '',
                            end: schedule.end || '',
                            location: schedule.location?.name || schedule.location || 'Unknown',
                            date: schedule.date,
                            breakDuration: schedule.break ? `${schedule.break} mins` : '',
                            careWorkerNames: careWorkerNames.length > 0 && careWorkerNames.some(name => name !== 'Unknown')
                                ? careWorkerNames
                                : ['Unknown'],
                            status: 'Upcoming' // Explicitly set to Upcoming as we filtered for it
                        };
                    })
                    .sort((a, b) => new Date(a.date) - new Date(b.date)); // Sort by date for proper display

                setWeeklyUpcomingSchedules(filteredWeeklyUpcoming);

            } catch (err) {
                console.error('Error fetching schedules:', err);
                const message = err.response?.status === 401
                    ? 'Not authorized. Please log in.'
                    : err.response?.data?.msg || 'Failed to load schedules';
                setError(message);
            }
        };

        // Only fetch when user data is available and not still loading
        if (!isLoading && user) {
            fetchAndFilterSchedules();
        }
    }, [user, isLoading, today]); // Dependencies for useEffect

     useEffect(() => {
        if (!user || !user.organization) {
            return;
        }

        const organization = user.organization;
        let message = null;

        if (organization.subscriptionStatus === 'expired') {
            message = "Your subscription has ended.";
        } else if (organization.subscriptionStatus === 'trial' && organization.trialEndDate) {
            const now = new Date();
            const trialEndDate = new Date(organization.trialEndDate);
            const timeDiff = trialEndDate.getTime() - now.getTime();
            const daysLeft = Math.ceil(timeDiff / (1000 * 60 * 60 * 24));

            if (daysLeft > 0) {
                message = `Your trial will expire in ${daysLeft} day${daysLeft > 1 ? 's' : ''}.`;
            } else {
                message = "Your 7-day free trial has expired.";
            }
        }
        setSubscriptionMessage(message);

    }, [user]);

useEffect(() => {
    const fetchSchedulesForDate = async () => {
        try {
            const dateStr = getDateKey(selectedDate);
            const response = await fetchSchedulesInRange(dateStr, dateStr);
            const rawSchedules = response?.data?.data || [];

            const filteredSchedules = rawSchedules
                .filter(schedule => {
                    if (user.role === 'admin' || user.role === 'manager') return true;

                    if (!schedule.careWorker) return false;

                    if (Array.isArray(schedule.careWorker)) {
                        return schedule.careWorker.some(cw =>
                            (typeof cw === 'object' && cw?._id === user.id) ||
                            (typeof cw === 'string' && cw === user.id)
                        );
                    }

                    return (typeof schedule.careWorker === 'object' && schedule.careWorker?._id === user.id) ||
                        (typeof schedule.careWorker === 'string' && schedule.careWorker === user.id);
                })
                .map(schedule => {
                    const careWorkerNames = Array.isArray(schedule.careWorker)
                        ? schedule.careWorker.map(cw =>
                            typeof cw === 'object'
                                ? `${cw.firstName || ''} ${cw.lastName || ''}`.trim()
                                : 'Unknown'
                        ).filter(Boolean)
                        : (schedule.careWorker?.firstName
                            ? [`${schedule.careWorker.firstName} ${schedule.careWorker.lastName || ''}`.trim()]
                            : ['Unknown']);

                    return {
                        id: schedule._id,
                        title: schedule.description || 'Shift',
                        start: schedule.start || '',
                        end: schedule.end || '',
                        location: schedule.location?.name || schedule.location || 'Unknown',
                        date: schedule.date,
                        breakDuration: schedule.break ? `${schedule.break} mins` : '',
                        careWorkerNames,
                        status: getScheduleStatus(schedule)
                    };
                });

            setSchedulesForDate(filteredSchedules);
        } catch (err) {
            console.error('❌ Failed to fetch schedules for date:', err);
        }
    };

    fetchSchedulesForDate();
}, [selectedDate, user]);





    // Helper to format time (e.g., "10:00 AM")
    const formatTime = (time) => {
        if (!time) return '';
        const parsedTime = parse(time, 'HH:mm', new Date());
        if (!isValid(parsedTime)) return time;
        return format(parsedTime, 'HH:mm');
    };

    // Filter schedules for today's display
    const completedSchedules = schedules.filter(schedule => schedule.status === 'Completed');
    const upcomingTodaySchedules = schedules.filter(schedule => schedule.status === 'Upcoming' || schedule.status === 'In Progress');

    // Loading state UI
    if (isLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-100 font-poppins">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                <p className="ml-3 text-lg text-gray-700">Loading user data...</p>
            </div>
        );
    }

    // Redirect if user is not logged in
    if (!user) {
        navigate('/login');
        return null;
    }

    return (
        <div className="min-h-screen bg-gray-100 font-poppins flex flex-col md:flex-row">
            {/* Sidebar / User Info Block */}
            

            {/* Main Content Area */}
            <main className="flex-1 px-4 md:px-8 pt-2 md:pt-4 pb-4 overflow-y-auto">
                <div className="flex justify-between items-center mb-3">
                    {/* <h1 className="text-3xl font-bold text-gray-800">Dashboard</h1> */}
                    {/* Subscription Message (if any) */}
                    {subscriptionMessage && isAdmin && (
                        <div className="flex-grow text-right ml-4">
                            <span className="bg-yellow-100 text-yellow-800 text-sm font-semibold px-4 py-2 rounded-full border border-yellow-300">
                                {subscriptionMessage}
                            </span>
                        </div>
                    )}
                </div>

                {/* Error Message Display */}
                {error && (
                    <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-md text-sm text-center">{error}</div>
                )}
                {isAdmin && (<div className="grid grid-cols-1 lg:grid-cols-3 gap-1">
    <LocationGraph />
  <BarGraph />
  <TotalHours/>
</div>)}

                {/* Dashboard Cards Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8 mt-4">
                    {/* Open Schedules Block */}
                    <div className="bg-white rounded-lg shadow-md p-6 flex flex-col justify-center border border-gray-200">
    {/* <img
      src="/openscheduleicon_.svg"
      alt="Open Schedule icon"
      
    /> */}

                        <h3 className="text-xl font-semibold text-black mb-2">Open Schedules</h3>
                        {openSchedules.length > 0 ? (
                            <div className="space-y-2 max-h-48 overflow-y-auto custom-scrollbar">
                                {openSchedules.map(schedule => (
                                    <div key={schedule.id} className="flex flex-col items-start text-gray-700 mb-2 p-2 rounded-md bg-gray-50 border border-gray-200">
                                         <img
      src="/openscheduleicon_.svg"
      alt="Open Schedule icon"
      className="mb-2"
    />
                        
                                        <div className="flex-1">
                                            <p className="font-medium">{schedule.title.charAt(0).toUpperCase() + schedule.title.slice(1)} </p>
                                            <p>{formatTime(schedule.start)} - {formatTime(schedule.end)}</p>
                                            <p className="text-sm text-gray-600">Location: {schedule.location}</p>
                                        </div>
                                        
                                        
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="flex items-center text-gray-700">
                                <span className="mr-2 text-xl"></span> {/* Placeholder for icon */}
                                <span className="font-medium">No open schedules available at this time.</span>
                            </div>
                        )}
                        <button
                            className="mt-4 text-blue-600 hover:underline self-end"
                            onClick={() => navigate('/openschedules')}
                        >
                            View All Open Schedules
                        </button>
                    </div>

                    {/* What's Happening Block (Today's Scheduled Shifts for the User/Admin) */}
                    <div className="bg-white rounded-lg shadow-md p-6 flex flex-col justify-center border border-gray-200">
                        <h3 className="text-xl font-semibold text-black mb-2">What's Happening Today</h3>
                        <div className="text-gray-700 max-h-48 overflow-y-auto custom-scrollbar">
                            {(completedSchedules.length > 0 || upcomingTodaySchedules.length > 0) ? (
                                <div className="space-y-4">
                                    {/* Completed Shifts Today */}
                                    {completedSchedules.length > 0 && (
                                        <div>
                                            <p className="text-sm font-semibold text-gray-800 mb-1">Completed</p>
                                            {completedSchedules.map(schedule => (
                                                <div key={schedule.id} className="text-xs text-gray-600 mb-2 bg-gray-100 p-2 rounded-md">
                                                    <p className="truncate">{schedule.title} ({formatTime(schedule.start)} - {formatTime(schedule.end)})</p>
                                                    <p className="truncate">Location: {schedule.location}</p>
                                                    <p className="truncate">Workers: {schedule.careWorkerNames.join(', ')}</p>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    {/* Upcoming & In Progress Shifts Today */}
                                    {upcomingTodaySchedules.length > 0 && (
                                        <div>
                                            <p className="text-sm font-semibold text-gray-800 mb-1">Upcoming & In Progress</p>
                                            {upcomingTodaySchedules.map(schedule => (
                                                <div key={schedule.id} className="text-xs text-gray-600 mb-2 bg-gray-50 p-2 rounded-md">
                                                    <p className="truncate">{schedule.title} ({formatTime(schedule.start)} - {formatTime(schedule.end)})</p>
                                                    <p className="truncate">Location: {schedule.location}</p>
                                                    <p className="truncate">Workers: {schedule.careWorkerNames.join(', ')}</p>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <p className="text-sm text-gray-600">No completed or upcoming schedules for today.</p>
                            )}
                        </div>
                    </div>

                    {/* Upcoming Weekly Schedules Block (Fixed for user roles) */}
                    {(user.role === 'admin' || user.role === 'manager' || user.role === 'careWorker' || user.role === 'employee') && (
                        <div className="bg-white rounded-lg shadow-md p-6 flex flex-col justify-center border border-gray-200">
                            <h3 className="text-xl font-semibold text-black mb-4">Upcoming Weekly Schedules</h3>
                            {weeklyUpcomingSchedules.length > 0 ? (
                                <div className="space-y-2 max-h-48 overflow-y-auto custom-scrollbar">
                                    {weeklyUpcomingSchedules.map(schedule => (
                                        <div key={schedule.id} className="flex items-start text-gray-700 mb-2 p-2 rounded-md bg-gray-50 border border-gray-200">
                                            {/* <span className="mr-2 text-xl">📅</span> */}
                                            <img src='./calendericon.svg' alt="Calendar icon" className='mr-2'/>
                                            <div className="flex-1">
                                                <p className="font-medium">
                                                    {schedule.title} on {format(new Date(schedule.date), 'EEE, MMM d')} </p>
                                                    <span>Time: {formatTime(schedule.start)} - {formatTime(schedule.end)}</span>
                                                <p className="text-sm text-gray-600">Location: {schedule.location}</p>
                                                {/* Only show workers for Admin/Manager roles */}
                                                {(user.role === 'admin' || user.role === 'manager') && (
                                                    <p className="text-sm text-gray-600">Workers: {schedule.careWorkerNames.join(', ')}</p>
                                                )}
                                            </div>
                                            
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="flex items-center text-gray-700">
                                    <span className="mr-2 text-xl"></span> {/* Placeholder for icon */}
                                    <span className="font-medium">No upcoming weekly schedules.</span>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Today's Schedule (Detailed List) - This is for the full breakdown of TODAY's shifts */}
               <div className="flex items-center justify-between mb-6">
  <h2 className="text-2xl font-semibold text-gray-800">
    Schedule for {getDisplayDate(selectedDate)}
  </h2>
  <div className="flex items-center bg-blue-600 rounded-md text-white text-xs sm:text-sm font-medium sm:p-[4px] sm:min-h-[40px]">
    <button
      onClick={() => setSelectedDate(prev => addDays(prev, -1))}
      className="px-1 sm:px-2 py-0 sm:py-1 hover:bg-gray-200 rounded-l-md transition-colors duration-200 bg-white text-black rounded-md min-h-[30px] sm:min-h-[35px]"
    >
      く
    </button>
    <button
  onClick={() => setSelectedDate(new Date())}
  className=" text-white-700  hover:text-white-800 px-4 py-1.5 rounded-md text-sm font-medium transition duration-200 shadow-sm"
>
   Today
</button>
    <button
      onClick={() => setSelectedDate(prev => addDays(prev, 1))}
      className="px-1 sm:px-2 py-0 sm:py-1  rounded-l-md transition-colors duration-200 bg-white text-black rounded-md min-h-[30px] sm:min-h-[35px] rotate-180"
    >
      く
    </button>
  </div>
</div>
{schedulesForDate.length > 0 ? (
  <div className="flex flex-wrap gap-5 justify-center sm:justify-start">
    {schedulesForDate.map(schedule => (
      <div
        key={schedule.id}
        className="bg-gray-50 p-4 rounded-md border border-gray-200 shadow-sm flex flex-col justify-between items-center space-y-4 w-full sm:w-auto min-w-[200px] max-w-[250px]"
      >
        <div>
          <p className="text-sm font-semibold text-gray-800 text-center">{schedule.title}</p>
          <p className="text-xs text-gray-600 text-center">
            {formatTime(schedule.start)} - {formatTime(schedule.end)}
          </p>
          <p className="text-xs text-gray-600 text-center">Date: {new Date(schedule.date).toLocaleDateString("en-GB")}</p>
          <p className="text-xs text-gray-600 text-center">Location: {schedule.location}</p>
          <p className="text-xs text-gray-600 text-center">Workers: {schedule.careWorkerNames.join(', ')}</p>
          {schedule.breakDuration && (
            <p className="text-xs text-gray-600 text-center">Break: {schedule.breakDuration}</p>
          )}
          <p className="text-xs text-gray-600 text-center">Status: {schedule.status}</p>
        </div>
        <button
          className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition duration-200 text-sm"
          onClick={() => navigate('/schedule')}
        >
          View Details
        </button>
      </div>
    ))}
  </div>
) : (
  <p className="text-sm text-gray-600 text-center">No assigned schedules for this day.</p>
)}





                {/* <div className="bg-white rounded-lg shadow-md p-6 mt-6">
  <h2 className="text-2xl font-semibold text-gray-800 mb-6">Tomorrow's Schedule ({tomorrow})</h2>
  {tomorrowSchedules.length > 0 ? (
    <div className="flex flex-wrap gap-5 justify-center sm:justify-start">
      {tomorrowSchedules.map(schedule => (
        <div
          key={schedule.id}
          className="bg-gray-50 p-4 rounded-md border border-gray-200 shadow-sm flex flex-col justify-between items-center space-y-4 w-full sm:w-auto min-w-[200px] max-w-[250px]"
        >
          <div>
            <p className="text-sm font-semibold text-gray-800 text-center">{schedule.title}</p>
            <p className="text-xs text-gray-600 text-center">
              {formatTime(schedule.start)} - {formatTime(schedule.end)}
            </p>
            <p className="text-xs text-gray-600 text-center">Location: {schedule.location}</p>
            <p className="text-xs text-gray-600 text-center">Workers: {schedule.careWorkerNames.join(', ')}</p>
            {schedule.breakDuration && (
              <p className="text-xs text-gray-600 text-center">Break: {schedule.breakDuration}</p>
            )}
            <p className="text-xs text-gray-600 text-center">Status: {schedule.status}</p>
          </div>
          <button
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition duration-200 text-sm"
            onClick={() => navigate('/schedule')}
          >
            View Details
          </button>
        </div>
      ))}
    </div>
  ) : (
    <p className="text-sm text-gray-600 text-center">No schedules for tomorrow.</p>
  )}
</div> */}

            </main>
        </div>
    );
};

export default MePage;




// import React, { useState, useEffect } from 'react';
// import { useNavigate } from 'react-router-dom';
// import { useAuth } from '../context/AuthContext'; // Ensure this path is correct
// import { fetchSchedulesInRange, fetchMyAssignedShifts } from '../api'; // Ensure this path is correct
// import { parse, format, isValid, isBefore, isAfter, isWithinInterval, addDays } from 'date-fns';
// import { toZonedTime } from 'date-fns-tz'; // Make sure you have date-fns-tz installed (npm install date-fns-tz)
// import '@fontsource/poppins'; // Make sure you have @fontsource/poppins installed (npm install @fontsource/poppins)

// const MePage = () => {
//     const { user, isLoading } = useAuth();
//     const navigate = useNavigate();
//     const [schedules, setSchedules] = useState([]); // For user's assigned schedules today
//     const [openSchedules, setOpenSchedules] = useState([]); // For general open schedules overview
//     const [weeklyUpcomingSchedules, setWeeklyUpcomingSchedules] = useState([]); // New state for weekly schedules
//     const [error, setError] = useState(null);
//     const [subscriptionMessage, setSubscriptionMessage] = useState(null); // Assuming you might have subscription messages

//     // Get today's date in IST for consistency
//     const today = format(toZonedTime(new Date(), 'Asia/Kolkata'), 'yyyy-MM-dd');

//     const isAdmin=user?.role === 'admin' || user?.role === 'manager';

//     // Helper function to determine schedule status (Completed, Upcoming, In Progress, Unknown)
//     const getScheduleStatus = (schedule) => {
//         if (!schedule.start || !schedule.end || !schedule.date) {
//             console.warn(`Missing date or times for schedule ${schedule.id}:`, schedule);
//             return 'Unknown';
//         }
//         try {
//             const timeZone = 'Asia/Kolkata';
//             const currentTime = toZonedTime(new Date(), timeZone);
//             const startDateTime = parse(`${schedule.date} ${schedule.start}`, 'yyyy-MM-dd HH:mm', new Date());
//             const endDateTime = parse(`${schedule.date} ${schedule.end}`, 'yyyy-MM-dd HH:mm', new Date());

//             if (!isValid(startDateTime) || !isValid(endDateTime)) {
//                 console.warn(`Invalid date/time for schedule ${schedule.id}:`, schedule);
//                 return 'Unknown';
//             }

//             const scheduleDate = parse(schedule.date, 'yyyy-MM-dd', new Date());
//             const todayDateOnly = parse(today, 'yyyy-MM-dd', new Date()); // For comparing just dates

//             // If schedule date is before today, it's completed
//             if (isBefore(scheduleDate, todayDateOnly)) {
//                 return 'Completed';
//             }

//             // If schedule date is today and current time is after end time, it's completed
//             if (format(scheduleDate, 'yyyy-MM-dd') === format(todayDateOnly, 'yyyy-MM-dd') && isAfter(currentTime, endDateTime)) {
//                 return 'Completed';
//             }

//             // If current time is before start time, it's upcoming
//             if (isBefore(currentTime, startDateTime)) return 'Upcoming';
//             // If current time is within the interval, it's in progress
//             if (isWithinInterval(currentTime, { start: startDateTime, end: endDateTime })) return 'In Progress';
//             // Fallback for other cases (should ideally be caught by above)
//             return 'Unknown';
//         } catch (e) {
//             console.error(`Error deriving status for schedule ${schedule.id}:`, e);
//             return 'Unknown';
//         }
//     };

//     // useEffect hook to fetch schedules when user data or loading state changes
//     useEffect(() => {
//         const fetchAndFilterSchedules = async () => {
//             if (!user || !user.id) {
//                 console.log('No user or user.id found', { user });
//                 setError('Please log in to view schedules');
//                 return;
//             }
//             setError(null); // Clear previous errors

//             try {
//                 console.log('Fetching schedules for user ID:', user.id, 'Role:', user.role);

//                 // Helper to get today's date in 'yyyy-MM-dd' IST format
//                 const getTodayDate = () => {
//                     const todayDate = toZonedTime(new Date(), 'Asia/Kolkata');
//                     return format(todayDate, 'yyyy-MM-dd');
//                 };

//                 // Helper to get a future date (e.g., 12 months from now) in 'yyyy-MM-dd' IST format
//                 const getFutureDate = (months) => {
//                     const futureDate = toZonedTime(new Date(), 'Asia/Kolkata');
//                     futureDate.setMonth(futureDate.getMonth() + months);
//                     return format(futureDate, 'yyyy-MM-dd');
//                 };

//                 // --- Fetch All Schedules in Range for Open Schedules and Today's Schedules ---
//                 // Admins/Managers fetch a broad range to see all potential shifts
//                 const startFetchDate = getTodayDate();
//                 const endFetchDate = getFutureDate(12); // Fetch schedules for the next 12 months

//                 // This call fetches all schedules within the range, which will be filtered client-side
//                 // for "What's Happening Today" and "Open Schedules" based on user role and assignment.
//                 const response = await fetchSchedulesInRange(startFetchDate, endFetchDate);
//                 const allFetchedSchedules = response.data.data || [];

//                 // --- Logic for Open Schedules Block ---
//                 const currentOpenSchedules = allFetchedSchedules
//                     .filter(schedule => {
//                         const scheduleDateObj = new Date(schedule.date);
//                         const todayDateObj = new Date(today);
//                         return (
//                             // Schedule date must be today or in the future
//                             (schedule.date && scheduleDateObj >= todayDateObj) &&
//                             // Schedule must not be assigned to any care worker (or assigned array is empty)
//                             (!schedule.careWorker ||
//                                 (Array.isArray(schedule.careWorker) && schedule.careWorker.length === 0)) &&
//                             // Schedule must be explicitly marked as not published (i.e., open)
//                             schedule.isPublished === false
//                         );
//                     })
//                     .map(schedule => ({
//                         id: schedule._id,
//                         title: schedule.description || 'Open Shift',
//                         start: schedule.start || '',
//                         end: schedule.end || '',
//                         location: schedule.location?.name || schedule.location || 'Unknown', // Handle nested location object
//                         date: schedule.date,
//                         breakDuration: schedule.break ? `${schedule.break} mins` : '',
//                         status: getScheduleStatus({ // Derive status for display
//                             id: schedule._id,
//                             start: schedule.start,
//                             end: schedule.end,
//                             date: schedule.date
//                         })
//                     }));
//                 setOpenSchedules(currentOpenSchedules);

//                 // --- Logic for User's Assigned Schedules (What's Happening & Today's Detailed Schedule) ---
//                 const userAssignedSchedulesToday = allFetchedSchedules
//                     .filter(schedule => {
//                         // Filter for schedules happening specifically today
//                         if (!schedule.date || schedule.date !== today) {
//                             return false;
//                         }

//                         if (user.role === 'admin' || user.role === 'manager') {
//                             // Admins/Managers see all schedules for today
//                             return true;
//                         } else {
//                             // Care workers/employees only see their own assigned schedules for today
//                             if (!schedule.careWorker) {
//                                 return false; // If no careWorker assigned, it's not for this user
//                             }
//                             if (Array.isArray(schedule.careWorker)) {
//                                 // Check if user's ID is in the careWorker array (can be object or string ID)
//                                 return schedule.careWorker.some(cw =>
//                                     (typeof cw === 'object' && cw?._id === user.id) ||
//                                     (typeof cw === 'string' && cw === user.id)
//                                 );
//                             }
//                             // If careWorker is a single object or string ID
//                             return (typeof schedule.careWorker === 'object' && schedule.careWorker?._id === user.id) ||
//                                 (typeof schedule.careWorker === 'string' && schedule.careWorker === user.id);
//                         }
//                     })
//                     .map(schedule => {
//                         // Determine care worker names for display
//                         const careWorkerNames = Array.isArray(schedule.careWorker)
//                             ? schedule.careWorker
//                                 .map(cw => {
//                                     if (typeof cw === 'object' && (cw?.firstName || cw?.lastName)) {
//                                         return `${cw.firstName || ''} ${cw.lastName || ''}`.trim();
//                                     }
//                                     return 'Unknown'; // Fallback for invalid worker object
//                                 })
//                                 .filter(Boolean) // Remove any empty strings from failed mappings
//                             : (schedule.careWorker?.firstName // If single careWorker object
//                                 ? [`${schedule.careWorker.firstName} ${schedule.careWorker.lastName || ''}`.trim()]
//                                 : ['Unknown']); // Fallback if single careWorker is not an object with name

//                         return {
//                             id: schedule._id,
//                             title: schedule.description || 'Shift',
//                             start: schedule.start || '',
//                             end: schedule.end || '',
//                             location: schedule.location?.name || schedule.location || 'Unknown',
//                             date: schedule.date,
//                             breakDuration: schedule.break ? `${schedule.break} mins` : '',
//                             // Ensure careWorkerNames is never empty or just ['Unknown'] if valid names exist
//                             careWorkerNames: careWorkerNames.length > 0 && careWorkerNames.some(name => name !== 'Unknown')
//                                 ? careWorkerNames
//                                 : ['Unknown'],
//                             status: getScheduleStatus({
//                                 id: schedule._id,
//                                 start: schedule.start,
//                                 end: schedule.end,
//                                 date: schedule.date
//                             })
//                         };
//                     });
//                 setSchedules(userAssignedSchedulesToday);


//                 // --- Logic for Weekly Upcoming Schedules ---
//                 let weeklyShiftsToProcess = [];
//                 // Calculate the end date for the 7-day window (today + 6 days)
//                 const sevenDaysFromNow = format(addDays(toZonedTime(new Date(), 'Asia/Kolkata'), 6), 'yyyy-MM-dd');
//                 const todayDateObj = parse(today, 'yyyy-MM-dd', new Date());

//                 if (user.role === 'admin' || user.role === 'manager') {
//                     // Admins/Managers see all upcoming shifts for the next 7 days from the broad fetch
//                     weeklyShiftsToProcess = allFetchedSchedules;
//                 } else if (user.role === 'careWorker' || user.role === 'employee') {
//                     const assignedResponse = await fetchMyAssignedShifts();
//                     const allMyAssignedShifts = assignedResponse.data.data || [];

//                     weeklyShiftsToProcess = allMyAssignedShifts.filter(schedule => {
//                         const scheduleDate = parse(schedule.date, 'yyyy-MM-dd', new Date());
//                         // Check if the schedule date is today or in the future
//                         const isTodayOrFuture = isAfter(scheduleDate, todayDateObj) || format(scheduleDate, 'yyyy-MM-dd') === format(todayDateObj, 'yyyy-MM-dd');
//                         const isWithinNextSevenDays = isBefore(scheduleDate, addDays(todayDateObj, 7));

//                         return isTodayOrFuture && isWithinNextSevenDays;
//                     });
//                 }

//                 const filteredWeeklyUpcoming = weeklyShiftsToProcess
//                     .filter(schedule => {
//                         // Filter for only truly 'Upcoming' shifts (not completed or in progress)
//                         const status = getScheduleStatus({
//                             id: schedule._id,
//                             start: schedule.start,
//                             end: schedule.end,
//                             date: schedule.date
//                         });
//                         return status === 'Upcoming';
//                     })
//                     .map(schedule => {
//                         // Determine care worker names for display, similar to the 'Today's Schedule' logic
//                         const careWorkerNames = Array.isArray(schedule.careWorker)
//                             ? schedule.careWorker
//                                 .map(cw => {
//                                     if (typeof cw === 'object' && (cw?.firstName || cw?.lastName)) {
//                                         return `${cw.firstName || ''} ${cw.lastName || ''}`.trim();
//                                     }
//                                     return 'Unknown';
//                                 })
//                                 .filter(Boolean)
//                             : (schedule.careWorker?.firstName
//                                 ? [`${schedule.careWorker.firstName} ${schedule.careWorker.lastName || ''}`.trim()]
//                                 : ['Unknown']);

//                         return {
//                             id: schedule._id,
//                             title: schedule.description || 'Shift',
//                             start: schedule.start || '',
//                             end: schedule.end || '',
//                             location: schedule.location?.name || schedule.location || 'Unknown',
//                             date: schedule.date,
//                             breakDuration: schedule.break ? `${schedule.break} mins` : '',
//                             careWorkerNames: careWorkerNames.length > 0 && careWorkerNames.some(name => name !== 'Unknown')
//                                 ? careWorkerNames
//                                 : ['Unknown'],
//                             status: 'Upcoming' // Explicitly set to Upcoming as we filtered for it
//                         };
//                     })
//                     .sort((a, b) => new Date(a.date) - new Date(b.date)); // Sort by date for proper display

//                 setWeeklyUpcomingSchedules(filteredWeeklyUpcoming);

//             } catch (err) {
//                 console.error('Error fetching schedules:', err);
//                 const message = err.response?.status === 401
//                     ? 'Not authorized. Please log in.'
//                     : err.response?.data?.msg || 'Failed to load schedules';
//                 setError(message);
//             }
//         };

//         // Only fetch when user data is available and not still loading
//         if (!isLoading && user) {
//             fetchAndFilterSchedules();
//         }
//     }, [user, isLoading, today]); // Dependencies for useEffect

//      useEffect(() => {
//         if (!user || !user.organization) {
//             return;
//         }

//         const organization = user.organization;
//         let message = null;

//         if (organization.subscriptionStatus === 'expired') {
//             message = "Your subscription has ended.";
//         } else if (organization.subscriptionStatus === 'trial' && organization.trialEndDate) {
//             const now = new Date();
//             const trialEndDate = new Date(organization.trialEndDate);
//             const timeDiff = trialEndDate.getTime() - now.getTime();
//             const daysLeft = Math.ceil(timeDiff / (1000 * 60 * 60 * 24));

//             if (daysLeft > 0) {
//                 message = `Your trial will expire in ${daysLeft} day${daysLeft > 1 ? 's' : ''}.`;
//             } else {
//                 message = "Your 7-day free trial has expired.";
//             }
//         }
//         setSubscriptionMessage(message);

//     }, [user]);


//     // Helper to format time (e.g., "10:00 AM")
//     const formatTime = (time) => {
//         if (!time) return '';
//         const parsedTime = parse(time, 'HH:mm', new Date());
//         if (!isValid(parsedTime)) return time;
//         return format(parsedTime, 'h:mm a');
//     };

//     // Filter schedules for today's display
//     const completedSchedules = schedules.filter(schedule => schedule.status === 'Completed');
//     const upcomingTodaySchedules = schedules.filter(schedule => schedule.status === 'Upcoming' || schedule.status === 'In Progress');

//     // Loading state UI
//     if (isLoading) {
//         return (
//             <div className="min-h-screen flex items-center justify-center bg-gray-100 font-poppins">
//                 <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
//                 <p className="ml-3 text-lg text-gray-700">Loading user data...</p>
//             </div>
//         );
//     }

//     // Redirect if user is not logged in
//     if (!user) {
//         navigate('/login');
//         return null;
//     }

//     return (
//         <div className="min-h-screen bg-gray-100 font-poppins flex flex-col md:flex-row">
//             {/* Sidebar / User Info Block */}
//             <aside
//                 className="w-full md:w-64 bg-white p-6 shadow-md border-b md:border-r border-gray-200 flex flex-col items-center py-8 flex-shrink-0"
//             >
//                 {/* Organization Name */}
//                 {user?.organization?.name && (
//                     <div className="flex items-center justify-center px-4 py-2 rounded-lg bg-gray-400 text-black shadow-lg transform transition duration-300 ease-in-out mb-4">
//                         <span className="text-base mr-2">🏢</span>
//                         <span className="text-sm font-bold tracking-wide">
//                             {user.organization.name}
//                         </span>
//                     </div>
//                 )}
//                 <div className="flex flex-col items-center flex-grow">
//                     {/* Profile Picture */}
//                     <div className="w-16 h-16 md:w-12 md:h-12 rounded-full overflow-hidden mb-4">
//                         <img
//                             src={user?.profilePicture || 'https://cdn.pixabay.com/photo/2015/10/05/22/37/blank-profile-picture-973460_1280.png'}
//                             alt={`${user?.firstName || 'User'}'s Profile`}
//                             className="w-full h-full object-cover"
//                             onError={(e) => { // Fallback for broken image
//                                 e.target.onerror = null;
//                                 e.target.src = 'https://cdn.pixabay.com/photo/2015/10/05/22/37/blank-profile-picture-973460_1280.png';
//                             }}
//                         />
//                     </div>
//                     {/* User Name and Role */}
//                     <h2 className="text-xl md:text-lg font-semibold text-gray-800 mb-1 text-center">
//                         {user?.firstName} {user?.lastName}
//                     </h2>
//                     <p className="text-base md:text-sm text-gray-600 mb-8 capitalize">
//                         <span className="font-semibold">Role:</span> {user?.role}
//                     </p>

                    
//                     {user.role === 'careWorker' && (
//                         <button
//                             className="w-full bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition duration-200 shadow-sm"
//                             onClick={() => navigate('/schedule')}
//                         >
//                             Start Shift
//                         </button>
//                     )}
//                 </div>
//             </aside>

//             {/* Main Content Area */}
//             <main className="flex-1 p-4 md:p-8 overflow-y-auto">
//                 <div className="flex justify-between items-center mb-8">
//                     <h1 className="text-3xl font-bold text-gray-800">Dashboard</h1>
//                     {/* Subscription Message (if any) */}
//                     {subscriptionMessage && isAdmin && (
//                         <div className="flex-grow text-right ml-4">
//                             <span className="bg-yellow-100 text-yellow-800 text-sm font-semibold px-4 py-2 rounded-full border border-yellow-300">
//                                 {subscriptionMessage}
//                             </span>
//                         </div>
//                     )}
//                 </div>

//                 {/* Error Message Display */}
//                 {error && (
//                     <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-md text-sm text-center">{error}</div>
//                 )}

//                 {/* Dashboard Cards Grid */}
//                 <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
//                     {/* Open Schedules Block */}
//                     <div className="bg-white rounded-lg shadow-md p-6 flex flex-col justify-center border border-gray-200">
//                         <h3 className="text-xl font-semibold text-gray-800 mb-2">Open Schedules</h3>
//                         {openSchedules.length > 0 ? (
//                             <div className="space-y-2 max-h-48 overflow-y-auto custom-scrollbar">
//                                 {openSchedules.map(schedule => (
//                                     <div key={schedule.id} className="flex items-center text-gray-700 mb-2 p-2 rounded-md bg-gray-50 border border-gray-200">
//                                         <span className="mr-2 text-xl">📋</span>
//                                         <div className="flex-1">
//                                             <p className="font-medium">{schedule.title} ({formatTime(schedule.start)} - {formatTime(schedule.end)})</p>
//                                             <p className="text-sm text-gray-600">Location: {schedule.location}</p>
//                                         </div>
                                        
                                        
//                                     </div>
//                                 ))}
//                             </div>
//                         ) : (
//                             <div className="flex items-center text-gray-700">
//                                 <span className="mr-2 text-xl"></span> {/* Placeholder for icon */}
//                                 <span className="font-medium">No open schedules available at this time.</span>
//                             </div>
//                         )}
//                         <button
//                             className="mt-4 text-blue-600 hover:underline self-end"
//                             onClick={() => navigate('/openschedules')}
//                         >
//                             View All Open Schedules
//                         </button>
//                     </div>

//                     {/* What's Happening Block (Today's Scheduled Shifts for the User/Admin) */}
//                     <div className="bg-white rounded-lg shadow-md p-6 flex flex-col justify-center border border-gray-200">
//                         <h3 className="text-xl font-semibold text-gray-800 mb-2">What's Happening Today</h3>
//                         <div className="text-gray-700 max-h-48 overflow-y-auto custom-scrollbar">
//                             {(completedSchedules.length > 0 || upcomingTodaySchedules.length > 0) ? (
//                                 <div className="space-y-4">
//                                     {/* Completed Shifts Today */}
//                                     {completedSchedules.length > 0 && (
//                                         <div>
//                                             <p className="text-sm font-semibold text-gray-800 mb-1">Completed</p>
//                                             {completedSchedules.map(schedule => (
//                                                 <div key={schedule.id} className="text-xs text-gray-600 mb-2 bg-gray-100 p-2 rounded-md">
//                                                     <p className="truncate">{schedule.title} ({formatTime(schedule.start)} - {formatTime(schedule.end)})</p>
//                                                     <p className="truncate">Location: {schedule.location}</p>
//                                                     <p className="truncate">Workers: {schedule.careWorkerNames.join(', ')}</p>
//                                                 </div>
//                                             ))}
//                                         </div>
//                                     )}
//                                     {/* Upcoming & In Progress Shifts Today */}
//                                     {upcomingTodaySchedules.length > 0 && (
//                                         <div>
//                                             <p className="text-sm font-semibold text-gray-800 mb-1">Upcoming & In Progress</p>
//                                             {upcomingTodaySchedules.map(schedule => (
//                                                 <div key={schedule.id} className="text-xs text-gray-600 mb-2 bg-gray-50 p-2 rounded-md">
//                                                     <p className="truncate">{schedule.title} ({formatTime(schedule.start)} - {formatTime(schedule.end)})</p>
//                                                     <p className="truncate">Location: {schedule.location}</p>
//                                                     <p className="truncate">Workers: {schedule.careWorkerNames.join(', ')}</p>
//                                                 </div>
//                                             ))}
//                                         </div>
//                                     )}
//                                 </div>
//                             ) : (
//                                 <p className="text-sm text-gray-600">No completed or upcoming schedules for today.</p>
//                             )}
//                         </div>
//                     </div>

//                     {/* Upcoming Weekly Schedules Block (Fixed for user roles) */}
//                     {(user.role === 'admin' || user.role === 'manager' || user.role === 'careWorker' || user.role === 'employee') && (
//                         <div className="bg-white rounded-lg shadow-md p-6 flex flex-col justify-center border border-gray-200">
//                             <h3 className="text-xl font-semibold text-gray-800 mb-2">Upcoming Weekly Schedules</h3>
//                             {weeklyUpcomingSchedules.length > 0 ? (
//                                 <div className="space-y-2 max-h-48 overflow-y-auto custom-scrollbar">
//                                     {weeklyUpcomingSchedules.map(schedule => (
//                                         <div key={schedule.id} className="flex items-center text-gray-700 mb-2 p-2 rounded-md bg-gray-50 border border-gray-200">
//                                             <span className="mr-2 text-xl">📅</span>
//                                             <div className="flex-1">
//                                                 <p className="font-medium">
//                                                     {schedule.title} on {format(new Date(schedule.date), 'EEE, MMM d')} ({formatTime(schedule.start)} - {formatTime(schedule.end)})
//                                                 </p>
//                                                 <p className="text-sm text-gray-600">Location: {schedule.location}</p>
//                                                 {/* Only show workers for Admin/Manager roles */}
//                                                 {(user.role === 'admin' || user.role === 'manager') && (
//                                                     <p className="text-sm text-gray-600">Workers: {schedule.careWorkerNames.join(', ')}</p>
//                                                 )}
//                                             </div>
                                            
//                                         </div>
//                                     ))}
//                                 </div>
//                             ) : (
//                                 <div className="flex items-center text-gray-700">
//                                     <span className="mr-2 text-xl"></span> {/* Placeholder for icon */}
//                                     <span className="font-medium">No upcoming weekly schedules.</span>
//                                 </div>
//                             )}
//                         </div>
//                     )}
//                 </div>

//                 {/* Today's Schedule (Detailed List) - This is for the full breakdown of TODAY's shifts */}
//                 <div className="bg-white rounded-lg shadow-md p-6">
//                     <h2 className="text-2xl font-semibold text-gray-800 mb-6">Today's Detailed Schedule ({today})</h2>
//                     {schedules.length > 0 ? (
//                         <div className="flex flex-wrap gap-5 justify-center sm:justify-start">
//                             {schedules.map(schedule => (
//                                 <div
//                                     key={schedule.id}
//                                     className="bg-gray-50 p-4 rounded-md border border-gray-200 shadow-sm flex flex-col justify-between items-center space-y-4 w-full sm:w-auto min-w-[200px] max-w-[250px]"
//                                 >
//                                     <div>
//                                         <p className="text-sm font-semibold text-gray-800 text-center">{schedule.title}</p>
//                                         <p className="text-xs text-gray-600 text-center">
//                                             {formatTime(schedule.start)} - {formatTime(schedule.end)}
//                                         </p>
//                                         <p className="text-xs text-gray-600 text-center">Location: {schedule.location}</p>
//                                         <p className="text-xs text-gray-600 text-center">Workers: {schedule.careWorkerNames.join(', ')}</p>
//                                         {schedule.breakDuration && (
//                                             <p className="text-xs text-gray-600 text-center">Break: {schedule.breakDuration}</p>
//                                         )}
//                                         <p className="text-xs text-gray-600 text-center">Status: {schedule.status}</p>
//                                     </div>
//                                     <button
//                                         className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition duration-200 text-sm"
//                                         onClick={() => navigate('/schedule')}
//                                     >
//                                         View Details
//                                     </button>
//                                 </div>
//                             ))}
//                         </div>
//                     ) : (
//                         <p className="text-sm text-gray-600 text-center">No assigned schedules for today.</p>
//                     )}
//                 </div>
//             </main>
//         </div>
//     );
// };

// export default MePage;